/**
 * NewsTab — news feed for the active symbol.
 * Hits /api/news/{symbol} which already exists in the router worker
 * (terminalRoutes.ts). Each row is headline + sentiment badge + source.
 */
import { useStore } from '@/store';
import { useQuery } from '@tanstack/react-query';
import { api, isSynthetic } from '@/lib/api';
import { GRN, RED, MUT, FONT } from '@/theme';

interface NewsItem {
  id:             string;
  headline:       string;
  summary:        string;
  source:         string;
  url:            string;
  publishedAt:    number;
  sentiment:      'bullish' | 'bearish' | 'neutral';
  sentimentScore: number;
}

const COLOR: Record<NewsItem['sentiment'], string> = {
  bullish: GRN, bearish: RED, neutral: MUT,
};

// URLs here come from a third-party news provider. A `javascript:` or
// `data:` href from a compromised or sloppy feed would execute in the
// user's session on click. Allow only http/https; anything else becomes
// inert rather than dangerous.
function safeHref(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  try {
    const u = new URL(raw, window.location.origin);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : undefined;
  } catch { return undefined; }
}

// Format a publish time that may be missing, in seconds, or in milliseconds.
// An unguarded `new Date(publishedAt)` stamps a missing/0 value as "Jan 1970"
// and treats UNIX seconds as milliseconds (also 1970). Accept both units;
// return '' when there's nothing real to show rather than a fake epoch date.
function fmtWhen(raw: unknown): string {
  const t = Number(raw);
  if (!Number.isFinite(t) || t <= 0) return '';
  const ms = t < 1e12 ? t * 1000 : t;   // < 1e12 => seconds, scale up
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export function NewsTab() {
  const focus = useStore(s => s.focusSymbol);
  const { data, isLoading, isError } = useQuery<NewsItem[]>({
    queryKey: ['news', focus],
    queryFn:  () => api(`/api/news/${encodeURIComponent(focus)}`),
    staleTime: 120_000,
  });

  if (isLoading) return <Centered>LOADING NEWS...</Centered>;
  if (isError)   return <Centered>NEWS UNAVAILABLE</Centered>;
  if (!data || data.length === 0) return <Centered>NO HEADLINES FOR {focus}</Centered>;

  return (
    <div style={{ fontFamily: FONT, fontSize: 11 }}>
      {/* Headlines can be model-generated when no news provider is
          configured. Sentiment badges on invented text look exactly like
          sentiment badges on real reporting — say which this is. */}
      {isSynthetic('/api/news/') && (
        <div style={{
          border: '1px solid #ff9900', background: 'rgba(255,153,0,0.10)',
          padding: '5px 9px', marginBottom: 6, fontSize: 10.5, color: '#ff9900',
        }}>
          <strong>SIMULATED HEADLINES.</strong>{' '}
          <span style={{ color: MUT }}>
            No news provider is connected — these stories and their sentiment
            scores are generated, not reported.
          </span>
        </div>
      )}
      {data.map(n => {
        // Partial feed rows can omit sentiment; an unguarded .toUpperCase()
        // there crashes the whole list. Anything unexpected reads as neutral.
        const sent = (n.sentiment === 'bullish' || n.sentiment === 'bearish' || n.sentiment === 'neutral')
          ? n.sentiment : 'neutral';
        const sentColor = COLOR[sent] ?? MUT;
        const when = fmtWhen(n.publishedAt);
        return (
          <a key={n.id} href={safeHref(n.url)} target="_blank" rel="noopener noreferrer" style={{
            display: 'block', padding: '6px 4px',
            borderBottom: '1px solid #1a1a1a',
            color: '#e0e0e0', textDecoration: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontSize: 9, color: sentColor, minWidth: 60,
                border: `1px solid color-mix(in srgb, ${sentColor} 20%, transparent)`,
                padding: '1px 4px', borderRadius: 2, textAlign: 'center',
                letterSpacing: 1,
              }}>{sent.toUpperCase()}</span>
              <span style={{ flex: 1 }}>{n.headline}</span>
              <span style={{ fontSize: 9, color: MUT, minWidth: 90, textAlign: 'right' }}>
                {[n.source, when].filter(Boolean).join(' · ')}
              </span>
            </div>
            {n.summary && (
              <div style={{ fontSize: 10, color: MUT, marginTop: 2, paddingLeft: 68 }}>
                {n.summary}
              </div>
            )}
          </a>
        );
      })}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: '#333', fontSize: 11, letterSpacing: 2,
      fontFamily: FONT,
    }}>{children}</div>
  );
}
